// Reużywalna karta produktu - zgodna z projektem
//@ts-nocheck

import React, { FC, useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Envelope } from 'react-bootstrap-icons';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSelector } from 'store';
import { IProductListItem, IUnit } from 'api/types';
import {
  usePostProductQuantityDecrement,
  usePostProductQuantityIncrement,
  usePostProductQuantity
} from 'api';
import { useRWD } from 'hooks';
import { Select, Link, Counter } from 'components/controls';
import { AddToCartButton, AddToShoppingListButton, AskProductModal } from 'components/containers';
import { useProductWithLabels, hasSpecialPriceLabel } from 'utils/productHelpers';
import LabelsList from 'components/controls/LabelsList';

import styles from 'theme/components/containers/ProductCard/ProductCard.module.scss';

interface IProps {
  product: IProductListItem;
  categoryId?: number;
  searchKeywords?: string;
  line?: boolean;
}

const ProductCard: FC<IProps> = ({ product, categoryId, searchKeywords, line }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.auth);
  const { isMobile } = useRWD();

  const productWithLabels = useProductWithLabels(product);

  // Stan hover
  const [isHovered, setIsHovered] = useState(false);

  // stan modala zapytania o produkt
  const [isAskProductModalOpen, setIsAskProductModalOpen] = useState(false);

  // Jednostki produktu - zawsze z danych produktu
  const units = product.product_unit_short_info?.units || [];

  // ID aktualnej jednostki - domyślnie z produktu
  const [unitId, setUnitId] = useState<number>(
    product.product_unit_short_info?.default_unit_id || 
    product.product_price_short_info?.prices?.[0]?.unit_id || 
    0
  );

  // Uaktualnienie jednostki gdy zmieni się produkt
  useEffect(() => {
    if (product.product_unit_short_info?.default_unit_id) {
      setUnitId(product.product_unit_short_info.default_unit_id);
    } else if (product.product_price_short_info?.prices?.[0]?.unit_id) {
      setUnitId(product.product_price_short_info.prices[0].unit_id);
    }
  }, [product.product_unit_short_info?.default_unit_id, product.product_price_short_info?.prices]);

  // aktualna ilość
  const [quantity, setQuantity] = useState(1);

  // aktualnie zmieniona ilość (sprzed walidacji)
  const [manuallyChangedQuantity, setManuallyChangedQuantity] = useState<number>();

  // aktualna jednostka
  const price = useMemo(
    () => product?.product_price_short_info?.prices?.find((price) => price.unit_id === unitId),
    [unitId, product]
  );

  // zmiana ilości po zmianie jednostki
  useEffect(() => {
    price &&
      setQuantity(
        product.product_unit_short_info?.units?.find((unit) => unitId === unit.unit_id)
          ?.minimal_quantity || 1
      );
  }, [price, unitId, product]);

  // pobranie informacji o zmniejszonej ilości
  const { mutate: decrementQuantity, isLoading: isDecrementQuantityLoading } =
    usePostProductQuantityDecrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    });

  // pobranie informacji o zwiększonej ilości
  const { mutate: incrementQuantity, isLoading: isIncrementQuantityLoading } =
    usePostProductQuantityIncrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    });

  // pobranie informacji ilości
  const {
    mutate: updateQuantity,
    mutateAsync: updateQuantityAsync,
    isLoading: isUpdateQuantityLoading
  } = usePostProductQuantity(product.id, {
    onSuccess: (data) => {
      setQuantity(data.data.value);
    }
  });

  const isOpenProfile = profile?.role === 'ROLE_OPEN_PROFILE';

  // Niebieska cena gdy API zwraca label "twoja cena" / "najlepsza cena"
  const hasPriceLabel = hasSpecialPriceLabel(price);

  // funkcja renderująca dropdown jednostek
  const renderUnitSelector = useCallback(() => {
    if (isOpenProfile || !price || !units || units.length === 0) {
      return null;
    }

    const isSingleUnit = units.length === 1;

    return (
      <div className={classnames(styles.unitSelectorWrapper, {
        [styles.unitSelectorSingle]: isSingleUnit
      })}>
        <Select<IUnit>
          onChange={(unit) => {
            if (unit?.unit_id) {
              setUnitId(unit.unit_id);
            }
          }}
          value={unitId}
          isDisabled={isSingleUnit}
          formatOptionLabel={(option) => {
            const unit = option.item;
            return (
              <div className={styles.selectLabelWrapper}>
                <div className={styles.selectLabelMain}>{unit.name}</div>
                <div className={styles.selectLabelSub}>
                  {unit.converter !== 1 ? `(${unit.converter})` : ''}
                </div>
              </div>
            );
          }}
          options={units.map((unit) => ({
            value: unit.unit_id,
            label: `${unit.name} ${unit.converter !== 1 ? `(${unit.converter})` : ''}`,
            item: unit
          }))}
        />
      </div>
    );
  }, [unitId, units, isOpenProfile, price]);

  // Renderowanie dla mobile - osobny layout
  if (isMobile) {
    return (
      <div
        className={classnames(
          styles.componentWrapper,
          styles.mobileLayout,
          'StylePath-Components-Containers-ProductCard',
          {
            [styles.openProfile]: isOpenProfile
          }
        )}
        itemProp="item"
        itemScope
        itemType="http://schema.org/Product">
        <LabelsList
          labels={productWithLabels.product_basic_short_info?.labels || []}
          className={styles.mobileLabelsWrapper}
        />
        {/* Górna część: obrazek po lewej, tytuł i INDEX po prawej */}
        <div className={styles.mobileTopSection}>
          {/* Obrazek produktu */}
          <div className={styles.mobileImageWrapper}>
            <Link
              to={`/${productWithLabels.product_basic_short_info?.url_link}`}
              state={{ categoryId, searchKeywords }}
              className={styles.mobileProductImage}
              style={{
                backgroundImage: `url(${product.product_image_formatted_short_info?.images?.[0]?.min})`
              }}>
            </Link>
            <meta
              itemProp="image"
              content={product.product_image_formatted_short_info?.images?.[0]?.min}
            />
          </div>

          {/* Tytuł i INDEX po prawej */}
          <div className={styles.mobileInfoSection}>
            <div className={styles.mobileProductTitle}>
              <Link
                className={styles.mobileTitle}
                to={`/${productWithLabels.product_basic_short_info?.url_link}`}
                state={{ categoryId, searchKeywords }}>
                <span itemProp="name">{productWithLabels.product_basic_short_info?.title}</span>
              </Link>
            </div>
            <div className={styles.mobileProductMeta}>
              <span>
                {t('INDEX')}: {productWithLabels.product_basic_short_info?.index}
              </span>
              {productWithLabels.product_basic_short_info?.producer_name && (
                <span>
                  {t('Producent')}: {productWithLabels.product_basic_short_info?.producer_name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Dolna część: wszystko na pełnej szerokości */}
        <div className={styles.mobileBottomSection}>
          {/* Select i Cena */}
          <div className={styles.mobileSelectAndPriceRow}>
            {renderUnitSelector()}
            <div className={styles.mobilePriceWrapper}>
              {isOpenProfile || !price ? (
                <div className={styles.mobilePrice}>
                  <Link
                    to={`/login?return_url=${encodeURIComponent(location.pathname + location.search)}`}
                    className={styles.loginToSeePriceLink}>
                    <strong className={styles.loginToSeePrice}>{t('Zaloguj się, aby poznać cenę')}</strong>
                  </Link>
                </div>
              ) : (
                <div className={classnames(styles.mobilePrice, { [styles.priceNetBlue]: hasPriceLabel })}>
                  <strong>{price.price_net_formatted} {price.currency}</strong>
                  <span className={styles.mobileNettoLabel}>({t('netto')})</span>
                </div>
              )}
            </div>
          </div>

          {/* Counter i przycisk koszyka */}
          <div className={styles.mobileCounterAndCartRow}>
            <div className={styles.mobileCounterWrapper}>
              <Counter
                onChange={(value) => updateQuantity({ value, unit_id: unitId })}
                onDecrease={() => decrementQuantity({ value: quantity, unit_id: unitId })}
                onIncrease={() => incrementQuantity({ value: quantity, unit_id: unitId })}
                value={quantity}
                onChangeValue={(isChanged, newValue) => setManuallyChangedQuantity(newValue)}
                disabled={
                  isOpenProfile ||
                  isDecrementQuantityLoading ||
                  isIncrementQuantityLoading ||
                  isUpdateQuantityLoading
                }
              />
            </div>
            <div className={styles.mobileCartButtonWrapper}>
              <AddToCartButton
                isQuantityChanges={!!manuallyChangedQuantity && manuallyChangedQuantity !== quantity}
                updateQuantity={async () => {
                  const newQuantity = manuallyChangedQuantity
                    ? await updateQuantityAsync({
                        value: manuallyChangedQuantity,
                        unit_id: unitId
                      })
                    : undefined;

                  return newQuantity?.data.value === manuallyChangedQuantity
                    ? manuallyChangedQuantity
                    : undefined;
                }}
                quantity={quantity}
                unitId={unitId}
                productId={product.id}
                disabled={
                  isOpenProfile ||
                  isDecrementQuantityLoading ||
                  isIncrementQuantityLoading
                }
              />
            </div>
          </div>

          {/* Divider */}
          <div className={styles.mobileDivider} />

          {/* Przyciski akcji */}
          <div className={styles.mobileActionButtonsRow}>
            {!isOpenProfile && (
              <div className={styles.mobileActionButtonLeft}>
                <AddToShoppingListButton
                  product_id={product.id}
                  unit_id={unitId}
                  image={product.product_image_formatted_short_info?.images?.[0]?.thumb}
                  title={product.product_basic_short_info?.title}
                  index={product.product_basic_short_info?.index}
                  currency={price?.currency}
                  quantity={quantity}
                  unit={price}
                />
              </div>
            )}
            <div
              className={styles.mobileActionButton}
              onClick={() => setIsAskProductModalOpen(true)}>
              <Envelope />
              <span>{t('Zapytaj o produkt')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Renderowanie dla desktop - oryginalny layout lub widok line
  return (
    <div
      className={classnames(styles.componentWrapper, 'StylePath-Components-Containers-ProductCard', {
        [styles.hovered]: isHovered && !line && !isOpenProfile,
        [styles.lineWrapper]: !!line,
        [styles.openProfile]: isOpenProfile
      })}
      onMouseEnter={() => !line && setIsHovered(true)}
      onMouseLeave={() => !line && setIsHovered(false)}
      itemProp="item"
      itemScope
      itemType="http://schema.org/Product">
      <LabelsList
        labels={productWithLabels.product_basic_short_info?.labels || []}
        className={styles.labelsWrapper}
      />
      {/* Obrazek produktu */}
      <div className={classnames(styles.imageWrapper, { [styles.imageWrapperLine]: !!line })}>
        <Link
          to={`/${productWithLabels.product_basic_short_info?.url_link}`}
          state={{ categoryId, searchKeywords }}
          className={classnames(styles.productImage, { [styles.productImageLine]: !!line })}
          style={{
            backgroundImage: `url(${product.product_image_formatted_short_info?.images?.[0]?.min})`
          }}>
        </Link>
        <meta
          itemProp="image"
          content={product.product_image_formatted_short_info?.images?.[0]?.min}
        />
      </div>

      {/* Szczegóły produktu */}
      <div className={classnames(styles.productDetails, { [styles.productDetailsLine]: !!line })}>
        {line ? (
          <>
            {/* Lewa część - tytuł, INDEX, Producent, opis */}
            <div className={styles.productInfoLeft}>
              {/* Tytuł produktu */}
              <div className={styles.productTitle}>
                <Link
                  className={styles.title}
                  to={`/${product.product_basic_short_info?.url_link}`}
                  state={{ categoryId, searchKeywords }}>
                  <span itemProp="name">{product.product_basic_short_info?.title}</span>
                </Link>
              </div>

              {/* INDEX i Producent - jeden pod drugim */}
              <div className={styles.productMeta}>
                <span>
                  {t('INDEX')}: {product.product_basic_short_info?.index}
                </span>
                {product.product_basic_short_info?.producer_name && (
                  <span>
                    {t('Producent')}: {product.product_basic_short_info?.producer_name}
                  </span>
                )}
              </div>

              {/* Opis produktu */}
              {product.product_basic_short_info?.description && (
                <div 
                  className={styles.productDescription}
                  dangerouslySetInnerHTML={{
                    __html: product.product_basic_short_info.description
                  }}
                />
              )}
            </div>
          </>
        ) : (
          <>
            {/* Tytuł produktu */}
            <div className={styles.productTitle}>
              <Link
                className={styles.title}
                to={`/${product.product_basic_short_info?.url_link}`}
                state={{ categoryId, searchKeywords }}>
                <span itemProp="name">{product.product_basic_short_info?.title}</span>
              </Link>
            </div>

            {/* INDEX i Producent - bez szarego tła */}
            <div className={styles.productMeta}>
              <span>
                {t('INDEX')}: {product.product_basic_short_info?.index}
              </span>
              {product.product_basic_short_info?.producer_name && (
                <span>
                  {t('Producent')}: {product.product_basic_short_info?.producer_name}
                </span>
              )}
            </div>
          </>
        )}

        {/* W widoku line - wszystko po prawej stronie */}
        {line ? (
          <div className={styles.lineRightSection}>
            {/* Cena */}
            <div className={styles.priceWrapperLine}>
              {isOpenProfile || !price ? (
                <div className={styles.priceLine}>
                  <Link
                    to={`/login?return_url=${encodeURIComponent(location.pathname + location.search)}`}
                    className={styles.loginToSeePriceLink}>
                    <strong className={styles.loginToSeePrice}>{t('Zaloguj się, aby poznać cenę')}</strong>
                  </Link>
                </div>
              ) : (
                <div className={classnames(styles.priceLine, { [styles.priceNetBlue]: hasPriceLabel })}>
                  <strong>{price.price_net_formatted} {price.currency}</strong>
                  <span className={styles.nettoLabel}>({t('netto')})</span>
                </div>
              )}
            </div>

            {/* Przyciski akcji */}
            <div className={styles.actionButtonsLine}>
              {!isOpenProfile && (
                <AddToShoppingListButton
                  product_id={product.id}
                  unit_id={unitId}
                  image={product.product_image_formatted_short_info?.images?.[0]?.thumb}
                  title={product.product_basic_short_info?.title}
                  index={product.product_basic_short_info?.index}
                  currency={price?.currency}
                  quantity={quantity}
                  unit={price}
                />
              )}
              <div
                className={styles.actionButton}
                onClick={() => setIsAskProductModalOpen(true)}>
                <Envelope />
                <span>{t('Zapytaj o produkt')}</span>
              </div>
            </div>

            {/* Select, Counter i przycisk koszyka */}
            <div className={styles.controlsLine}>
              {renderUnitSelector()}
              <div className={styles.counterWrapperLine}>
                <Counter
                  onChange={(value) => updateQuantity({ value, unit_id: unitId })}
                  onDecrease={() => decrementQuantity({ value: quantity, unit_id: unitId })}
                  onIncrease={() => incrementQuantity({ value: quantity, unit_id: unitId })}
                  value={quantity}
                  onChangeValue={(isChanged, newValue) => setManuallyChangedQuantity(newValue)}
                  disabled={
                    isOpenProfile ||
                    isDecrementQuantityLoading ||
                    isIncrementQuantityLoading ||
                    isUpdateQuantityLoading
                  }
                />
              </div>
              <div className={styles.cartButtonWrapperLine}>
                <AddToCartButton
                  isQuantityChanges={!!manuallyChangedQuantity && manuallyChangedQuantity !== quantity}
                  updateQuantity={async () => {
                    const newQuantity = manuallyChangedQuantity
                      ? await updateQuantityAsync({
                          value: manuallyChangedQuantity,
                          unit_id: unitId
                        })
                      : undefined;

                    return newQuantity?.data.value === manuallyChangedQuantity
                      ? manuallyChangedQuantity
                      : undefined;
                  }}
                  quantity={quantity}
                  unitId={unitId}
                  productId={product.id}
                  disabled={
                    isOpenProfile ||
                    isDecrementQuantityLoading ||
                    isIncrementQuantityLoading
                  }
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Select i Cena w jednej linii - bez przycisku koszyka */}
            <div className={styles.selectAndPriceRow}>
              {renderUnitSelector()}
              <div className={styles.priceWrapper}>
                {price ? (
                  <div className={classnames(styles.price, { [styles.priceNetBlue]: hasPriceLabel })}>
                    <strong>{price.price_net_formatted} {price.currency}</strong>
                    <span className={styles.nettoLabel}>({t('netto')})</span>
                  </div>
                ) : (
                  <div className={styles.price}>
                    <Link
                      to={`/login?return_url=${encodeURIComponent(location.pathname + location.search)}`}
                      className={styles.loginToSeePriceLink}>
                      <strong className={styles.loginToSeePrice}>{t('Zaloguj się, aby poznać cenę')}</strong>
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Sekcja hover - Counter, przycisk koszyka, linia i przyciski akcji */}
            <div className={styles.hoverSection}>
              <div className={styles.counterAndCartRow}>
                <div className={styles.counterWrapper}>
                  <Counter
                    className={styles.quantityCounter}
                    onChange={(value) => updateQuantity({ value, unit_id: unitId })}
                    onDecrease={() => decrementQuantity({ value: quantity, unit_id: unitId })}
                    onIncrease={() => incrementQuantity({ value: quantity, unit_id: unitId })}
                    value={quantity}
                    onChangeValue={(isChanged, newValue) => setManuallyChangedQuantity(newValue)}
                    disabled={
                      isOpenProfile ||
                      isDecrementQuantityLoading ||
                      isIncrementQuantityLoading ||
                      isUpdateQuantityLoading
                    }
                  />
                </div>
                <div className={styles.cartButtonWrapper}>
                  <AddToCartButton
                    isQuantityChanges={!!manuallyChangedQuantity && manuallyChangedQuantity !== quantity}
                    updateQuantity={async () => {
                      const newQuantity = manuallyChangedQuantity
                        ? await updateQuantityAsync({
                            value: manuallyChangedQuantity,
                            unit_id: unitId
                          })
                        : undefined;

                      return newQuantity?.data.value === manuallyChangedQuantity
                        ? manuallyChangedQuantity
                        : undefined;
                    }}
                    quantity={quantity}
                    unitId={unitId}
                    productId={product.id}
                    disabled={
                      isOpenProfile ||
                      isDecrementQuantityLoading ||
                      isIncrementQuantityLoading
                    }
                  />
                </div>
              </div>

              <div className={styles.divider} />

              <div className={styles.actionButtonsRow}>
                {!isOpenProfile && (
                  <div className={styles.actionButtonLeft}>
                    <AddToShoppingListButton
                      product_id={product.id}
                      unit_id={unitId}
                      image={product.product_image_formatted_short_info?.images?.[0]?.thumb}
                      title={product.product_basic_short_info?.title}
                      index={product.product_basic_short_info?.index}
                      currency={price?.currency}
                      quantity={quantity}
                      unit={price}
                    />
                  </div>
                )}
                <div
                  className={styles.actionButton}
                  onClick={() => setIsAskProductModalOpen(true)}>
                  <Envelope />
                  <span>{t('Zapytaj o produkt')}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <AskProductModal
        product={product as any}
        open={isAskProductModalOpen}
        onClose={() => setIsAskProductModalOpen(false)}
      />
    </div>
  );
};

export default ProductCard;
